import React, { useState } from 'react';
import { MapPin, Search, ChevronDown, ChevronUp } from 'lucide-react';

export default function LocationInventoryPanel({ inventoryByLocation, thresholds }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('location'); // 'location', 'noir_stock', 'bleu_stock'
  const [sortOrder, setSortOrder] = useState('asc');
  const [expandedLocations, setExpandedLocations] = useState(new Set());

  const getStockStatus = (available, type) => {
    const threshold = thresholds?.[type];
    if (available <= threshold?.critical) return 'critical';
    if (available <= threshold?.low) return 'warning';
    return 'good';
  };

  const getStatusColor = (status) => {
    const colors = {
      good: 'bg-green-100 text-green-800',
      warning: 'bg-yellow-100 text-yellow-800',
      critical: 'bg-red-100 text-red-800'
    };
    return colors?.[status] || colors?.good;
  };

  const toggleLocation = (location) => {
    const newExpanded = new Set(expandedLocations);
    if (newExpanded?.has(location)) {
      newExpanded?.delete(location);
    } else {
      newExpanded?.add(location);
    }
    setExpandedLocations(newExpanded);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const filteredAndSortedLocations = inventoryByLocation
    ?.filter(loc => 
      loc?.location?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    )
    ?.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'noir_stock':
          aVal = a?.noir_270l?.available;
          bVal = b?.noir_270l?.available;
          break;
        case 'bleu_stock':
          aVal = a?.bleu_220l?.available;
          bVal = b?.bleu_220l?.available;
          break;
        default:
          aVal = a?.location;
          bVal = b?.location;
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MapPin size={20} className="text-green-600" />
          <h2 className="text-lg font-semibold text-gray-900">Inventaire par Emplacement</h2>
        </div>
        <span className="text-sm text-gray-600">{filteredAndSortedLocations?.length} emplacements</span>
      </div>
      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un emplacement..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('location')}
              >
                <div className="flex items-center gap-1">
                  Emplacement
                  {sortBy === 'location' && (
                    sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                  )}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('noir_stock')}
              >
                <div className="flex items-center gap-1">
                  Noir 270L
                  {sortBy === 'noir_stock' && (
                    sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                  )}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('bleu_stock')}
              >
                <div className="flex items-center gap-1">
                  Bleu 220L
                  {sortBy === 'bleu_stock' && (
                    sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                  )}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                Statut
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredAndSortedLocations?.map((loc, index) => {
              const noirStatus = getStockStatus(loc?.noir_270l?.available, 'noir_270l');
              const bleuStatus = getStockStatus(loc?.bleu_220l?.available, 'bleu_220l');
              const overallStatus = noirStatus === 'critical' || bleuStatus === 'critical' ? 'critical' : 
                                    noirStatus === 'warning' || bleuStatus === 'warning' ? 'warning' : 'good';
              const isExpanded = expandedLocations?.has(loc?.location);

              return (
                <React.Fragment key={index}>
                  <tr 
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => toggleLocation(loc?.location)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MapPin size={16} className="text-gray-400" />
                        <span className="font-medium text-gray-900">{loc?.location}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{loc?.noir_270l?.available}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(noirStatus)}`}>
                          {loc?.noir_270l?.total}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{loc?.bleu_220l?.available}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(bleuStatus)}`}>
                          {loc?.bleu_220l?.total}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(overallStatus)}`}>
                        {overallStatus === 'critical' ? 'Critique' : overallStatus === 'warning' ? 'Attention' : 'Normal'}
                      </span>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-gray-50">
                      <td colSpan="4" className="px-4 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Noir 270L Details */}
                          <div className="bg-white rounded-lg border border-gray-200 p-4">
                            <h4 className="font-medium text-gray-900 mb-3">Barrique Noire 270L</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Stock Total:</span>
                                <span className="font-medium text-gray-900">{loc?.noir_270l?.total}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Disponible:</span>
                                <span className="font-semibold text-green-600">{loc?.noir_270l?.available}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Réservé:</span>
                                <span className="font-medium text-orange-600">{loc?.noir_270l?.reserved}</span>
                              </div>
                              <div className="flex justify-between pt-2 border-t border-gray-200">
                                <span className="text-gray-600">Taux disponibilité:</span>
                                <span className="font-medium text-gray-900">
                                  {loc?.noir_270l?.total > 0 ? Math.round((loc?.noir_270l?.available / loc?.noir_270l?.total) * 100) : 0}%
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Bleu 220L Details */}
                          <div className="bg-white rounded-lg border border-gray-200 p-4">
                            <h4 className="font-medium text-gray-900 mb-3">Barrique Bleue 220L</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Stock Total:</span>
                                <span className="font-medium text-gray-900">{loc?.bleu_220l?.total}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Disponible:</span>
                                <span className="font-semibold text-green-600">{loc?.bleu_220l?.available}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Réservé:</span>
                                <span className="font-medium text-orange-600">{loc?.bleu_220l?.reserved}</span>
                              </div>
                              <div className="flex justify-between pt-2 border-t border-gray-200">
                                <span className="text-gray-600">Taux disponibilité:</span>
                                <span className="font-medium text-gray-900">
                                  {loc?.bleu_220l?.total > 0 ? Math.round((loc?.bleu_220l?.available / loc?.bleu_220l?.total) * 100) : 0}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {filteredAndSortedLocations?.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <MapPin size={48} className="mx-auto mb-2 text-gray-300" />
            <p>Aucun emplacement trouvé</p>
          </div>
        )}
      </div>
    </div>
  );
}